import type { CssGeneratorContext } from '../css-generator.model';

export const styles = ({ dt }: CssGeneratorContext) => `.user-info-trigger {
  padding: ${dt('userInfo.trigger.padding')};
  border: 1px solid ${dt('userInfo.trigger.closed.borderColor')};
  border-radius: ${dt('userInfo.trigger.borderRadius')};
  background: ${dt('userInfo.trigger.closed.background')};
  font: inherit;

  & .p-avatar {
    width: ${dt('userInfo.trigger.avatar.size')};
    height: ${dt('userInfo.trigger.avatar.size')};
    background: ${dt('userInfo.trigger.closed.avatarBackground')};
    color: ${dt('userInfo.trigger.closed.avatarColor')};
    font-size: ${dt('userInfo.trigger.avatar.fontSize')};
    font-weight: ${dt('userInfo.trigger.avatar.fontWeight')};
    box-shadow: inset 0 0 0 ${dt('userInfo.trigger.avatar.ringWidth')} ${dt('userInfo.trigger.closed.avatarRingColor')};
  }

  & .name {
    color: ${dt('userInfo.trigger.closed.nameColor')};
    font-size: ${dt('userInfo.trigger.name.fontSize')};
    line-height: ${dt('userInfo.trigger.name.lineHeight')};
    font-weight: ${dt('userInfo.trigger.name.fontWeight')};
  }

  & .detail {
    color: ${dt('userInfo.trigger.closed.detailColor')};
    font-size: ${dt('userInfo.trigger.detail.fontSize')};
    line-height: ${dt('userInfo.trigger.detail.lineHeight')};
  }

  & .pi {
    color: ${dt('userInfo.trigger.closed.iconColor')};
    font-size: ${dt('userInfo.trigger.icon.size')};
  }
}

.user-info-panel {
  position: absolute;
  z-index: ${dt('userInfo.panel.zIndex')};
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  transition:
    grid-template-rows ${dt('transition.duration')} ease-out,
    visibility 0s linear ${dt('transition.duration')};

  & > * {
    overflow: hidden;
    min-height: 0;
    border: 1px solid ${dt('userInfo.panel.borderColor')};
    background: ${dt('userInfo.panel.background')};
    box-shadow: ${dt('userInfo.panel.shadow')};
  }
}

.user-info-panel.user-info-panel--open {
  grid-template-rows: 1fr;
  visibility: visible;
  transition: grid-template-rows ${dt('transition.duration')} ease-out;
}

@media screen and (max-width: 767px) {
  .user-info-trigger {
    padding: 0;

    & .p-avatar {
      width: ${dt('userInfo.trigger.mobile.avatar.size')};
      height: ${dt('userInfo.trigger.mobile.avatar.size')};
      font-size: ${dt('userInfo.trigger.mobile.avatar.fontSize')};
    }
  }
}

@media screen and (min-width: 768px) {
  .user-info-trigger {
    transition:
      border-radius ${dt('transition.duration')} ease-out,
      border-color ${dt('transition.duration')} ease-out,
      background-color ${dt('transition.duration')} ease-out;

    & .p-avatar {
      transition:
        background-color ${dt('transition.duration')} ease-out,
        color ${dt('transition.duration')} ease-out,
        box-shadow ${dt('transition.duration')} ease-out;
    }

    & .name,
    & .detail {
      transition: color ${dt('transition.duration')} ease-out;
    }

    & .pi {
      transition:
        color ${dt('transition.duration')} ease-out,
        transform ${dt('transition.duration')} ease-out;
    }

    &:hover,
    &:focus-visible {
      background: ${dt('userInfo.trigger.closed.hoverBackground')};
    }

    &[aria-expanded="true"] .pi {
      transform: rotate(180deg);
    }
  }
}

@media screen and (max-width: 1535px) {
  .user-info-panel {
    top: calc(100% + ${dt('userInfo.panel.mobile.offset')});
    right: 0;
    width: ${dt('userInfo.menu.width')};
    max-width: calc(100vw - 2rem);

    & > * {
      border-radius: ${dt('userInfo.panel.borderRadius')};
    }
  }
}

@media screen and (min-width: 1536px) {
  .user-info-trigger {
    width: ${dt('userInfo.menu.width')};

    &[aria-expanded="true"] {
      border-color: ${dt('userInfo.trigger.open.borderColor')};
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      background: ${dt('userInfo.trigger.open.background')};
      box-shadow: ${dt('userInfo.panel.shadow')};

      & .p-avatar {
        background: ${dt('userInfo.trigger.open.avatarBackground')};
        color: ${dt('userInfo.trigger.open.avatarColor')};
        box-shadow: inset 0 0 0 ${dt('userInfo.trigger.avatar.ringWidth')} ${dt('userInfo.trigger.open.avatarRingColor')};
      }

      & .name {
        color: ${dt('userInfo.trigger.open.nameColor')};
      }

      & .detail {
        color: ${dt('userInfo.trigger.open.detailColor')};
      }

      & .pi {
        color: ${dt('userInfo.trigger.open.iconColor')};
      }
    }
  }

  .user-info-panel {
    top: 100%;
    left: 0;
    right: 0;

    & > * {
      border-top: 0;
      border-radius: 0 0 ${dt('userInfo.panel.borderRadius')} ${dt('userInfo.panel.borderRadius')};
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .user-info-panel,
  .user-info-panel.user-info-panel--open,
  .user-info-trigger,
  .user-info-trigger .p-avatar,
  .user-info-trigger .name,
  .user-info-trigger .detail,
  .user-info-trigger .pi {
    transition: none;
  }
}

.user-info-panel-header {
  padding: ${dt('userInfo.header.padding')};

  & .avatar {
    font-weight: ${dt('userInfo.header.avatar.fontWeight')};
  }

  & .name {
    color: ${dt('userInfo.header.name.color')};
    font-size: ${dt('userInfo.header.name.fontSize')};
    line-height: ${dt('userInfo.header.name.lineHeight')};
    font-weight: ${dt('userInfo.header.name.fontWeight')};
  }

  & .detail {
    color: ${dt('userInfo.header.detail.color')};
    font-size: ${dt('userInfo.header.detail.fontSize')};
    line-height: ${dt('userInfo.header.detail.lineHeight')};
  }
}

.user-info-panel-nav {
  padding: ${dt('userInfo.nav.padding')};
  gap: ${dt('userInfo.nav.gap')};
}

.user-info-panel-item {
  padding: ${dt('userInfo.item.padding')};
  border: 0;
  border-radius: ${dt('userInfo.item.borderRadius')};
  background: transparent;
  color: ${dt('userInfo.item.color')};
  font: inherit;
  font-size: ${dt('userInfo.item.fontSize')};
  line-height: ${dt('userInfo.item.lineHeight')};
  font-weight: ${dt('userInfo.item.fontWeight')};
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  & .pi {
    color: ${dt('userInfo.item.iconColor')};
    font-size: ${dt('userInfo.item.iconSize')};
  }

  &:hover {
    background: ${dt('userInfo.item.hoverBackground')};
  }

  &:focus-visible {
    outline: ${dt('focus.ring.width')} ${dt('focus.ring.style')} ${dt('focus.ring.color')};
    outline-offset: ${dt('focus.ring.offset')};
  }

  &.sign-out {
    color: ${dt('userInfo.signOut.color')};
    font-weight: ${dt('userInfo.signOut.fontWeight')};

    & .pi {
      color: inherit;
    }

    &:hover {
      background: ${dt('userInfo.signOut.hoverBackground')};
    }
  }
}
`;
